import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SelectField from "../../components/inputs/select";
import Basic from "../../components/inputs/basic";
import Checkbox from "../../components/inputs/checkbox";
import FieldGroup from "../../components/inputs/fieldGroup";
import Button from "../../components/inputs/button";
import Toast from "../../components/toast";
import {
	acknowledgeJob,
	cancelJob,
	getJob,
	getJobDownload,
	getOptions,
	getOrderOptions,
	getUnacknowledgedFailures,
	submitJob,
	toApiFilters,
} from "../../services/bulkReports";
import { getSession, getUserId } from "../../services/auth";
import { getTags } from "../../services/tag";
import { canCancelBulkReport } from "../../utilities/permissions";
import { BULK_REPORT_ERRORS, bulkReportErrorCode, bulkReportErrorMessage } from "./bulkReportErrors";
import { campaignLabel, failureToastMessage, formatDay, isActiveJob, specValues } from "./bulkReportJobs";
import BulkReportJobsTable from "./bulkReportJobsTable";
import useBulkReportJobs from "./useBulkReportJobs";

// Bulk Reporting Tool (ASB-3991). Lives under Audience Insights > Activation
// beside Campaign Profile and Audience Performance, which are QuickSight
// embeds; this page is native so it can take a longer date range than the
// dashboard allows and hand back CSV files.
//
// ASB-3993: route, title and description. ASB-3994: the Order selector, the
// ArcTag's campaigns from GET /bulk-reports/options, with the read-only
// Advertiser line. ASB-3995: Date Range Start / End seeded from the
// campaign's flight and bounded to the data window, plus the Line Item /
// Country / Device / Domain / Event Type filters. ASB-3996: the six data-type
// checkboxes, hidden until a campaign is chosen and unchecked when they
// appear.
//
// ASB-4295 (ASB-3997, ASB-3998): every report is a background job. Download
// My Report and Email Me My Report queue one (POST /bulk-reports/jobs) and
// the reports table under the form follows it; a Download job that finishes
// while the page is open downloads through a fresh short-lived link. A
// report that failed while the user was away toasts on their next visit.
// ?job=<id> (the emails' link) highlights that report, once (ASB-4365).
// ASB-4727: orders and line items go by GAM ID; a job is named by its order_label.

export const BULK_REPORTING_TOOL_ID = "activation bulk reporting tool";
export const BULK_REPORTING_TOOL_PATH = "/audience-insights/activation-bulk-reporting-tool";
export const BULK_REPORTING_TOOL_TITLE = "Campaign Profile: Bulk Reporting Tool";
export const BULK_REPORTING_TOOL_DESCRIPTION =
	"Use this tool to download your performance data from the AMS Platform to enable your offline analysis.";
// The tool's Help Center page (ASB-4118, arcspan_docs), opened in the portal's
// help view.
export const BULK_REPORTING_TOOL_HELP_PATH =
	"/help/docs/audience-insights/activation-insights/bulk-reporting-tool.html";

// The six export sections, in the order the story lists them. The values are
// the API's section keys.
export const DATA_TYPES = [
	{ value: "engagement", label: "Engagement" },
	{ value: "technographics", label: "Technographics" },
	{ value: "audience_overlap", label: "Audience Overlap" },
	{ value: "demographics", label: "Demographics" },
	{ value: "third_party", label: "Third Party" },
	{ value: "content_read", label: "Content Read" },
];

// Campaign Profile's dimension filters. `source` is the per-order options key.
const FILTERS = [
	{ key: "lineItem", label: "Line Item", source: "line_items", all: "All line items" },
	{ key: "country", label: "Country", source: "countries", all: "All countries" },
	{ key: "device", label: "Device", source: "devices", all: "All devices" },
	{ key: "domain", label: "Domain", source: "domains", all: "All domains" },
];
const EMPTY_FILTERS = { lineItem: [], country: [], device: [], domain: [] };

// Event Type is one value or none: "All" sends no event_type filter.
const ALL_EVENT_TYPES = { value: "", label: "All" };
const EVENT_TYPE_LABELS = { view: "View", click: "Click" };

// Line items arrive as { value: ID, label }; the other filters as plain strings.
const toOptions = (values) =>
	(values || []).map((v) => (v && typeof v === "object" ? v : { value: v, label: v }));
const toEventTypeOptions = (values) => [
	ALL_EVENT_TYPES,
	...(values || []).map((v) => ({ value: v, label: EVENT_TYPE_LABELS[v] || v })),
];

// demo.js shows this when the ArcTag read fails, since the publisher's
// setting (and so access to the page) is then unknown.
export const BULK_REPORTING_TOOL_LOAD_ERROR =
	"This page could not be loaded. Refresh the page to try again. If it continues, contact AMS support.";
export const ORDERS_ERROR = "Campaigns could not be loaded.";
export const NO_CAMPAIGNS = "No campaigns available";
export const ORDER_OPTIONS_ERROR = "This campaign's dates and filters could not be loaded.";
export const RANGE_ERROR = "Start date is after the end date.";
export const DATA_TYPES_ERROR = "At least one data type is required.";

// ASB-4295 copy. The first four are the stories' and the brief's wording.
export const REPORT_DOWNLOADING = "Your report is ready and downloading.";
export const EMAIL_REQUESTED =
	"We're generating your report now. You'll get an email with a download link when it's ready.";
export const REUSED_DOWNLOAD = "A matching report from the last 24 hours is ready.";
export const REUSED_EMAIL = "A matching report from the last 24 hours is ready. We've emailed you the link.";
export const GENERATION_FAILED =
	"The report could not be generated. Try again. If it fails again, contact AMS support.";
export const DEEP_LINK_MISSING = "The report in this link could not be found, or you do not have access to it.";
// The app keeps no list of the user's ArcTags outside the header, so the
// message cannot name the other one (and never shows its silo id).
export const DEEP_LINK_OTHER_TAG =
	"This report is on another ArcTag. Switch ArcTag from the menu at the top of the page to see it.";
export const CANCEL_NOT_OWNER = "You can only cancel reports you requested.";
export const GENERATE_AGAIN_MISSING = "This report's campaign is no longer available. Choose another campaign.";

// A Download job downloads by itself only when this page saw the finish
// live: it last saw the job queued or running no more than 2 minutes before it
// saw it succeeded, both by the browser's own clock (the server's finished_at
// is never compared with it, so clock skew cannot matter). Otherwise (the tab
// was hidden, the user was away) the report waits in the table with a toast.
export const AUTO_DOWNLOAD_WINDOW_MS = 2 * 60 * 1000;
export const REPORT_READY_IN_TABLE = "Your report is ready. Download it from the Reports table below.";

// The browser fetches the presigned link itself, in a hidden frame of its own.
// S3 answers it with Content-Disposition: attachment. Navigating the page to it
// instead let a second download cancel the first when two reports finished
// together, and an S3 error page replaced the app. An error now stays inside
// the frame; the row's Download asks for a fresh link. The frame is removed
// once the browser has long since taken over the download.
export const DOWNLOAD_FRAME_MS = 60 * 1000;
const openDownload = (url) => {
	const frame = document.createElement("iframe");
	frame.hidden = true;
	frame.setAttribute("aria-hidden", "true");
	frame.className = "bulk-report-download-frame";
	frame.src = url;
	document.body.appendChild(frame);
	setTimeout(() => frame.remove(), DOWNLOAD_FRAME_MS);
};

// YYYY-MM-DD, clamped into [from, to]. ISO dates compare as strings.
export const clampDate = (date, from, to) => {
	if (!date) return date;
	if (from && date < from) return from;
	if (to && date > to) return to;
	return date;
};

// "Mar 17, 2026" (bulkReportJobs.js; the table prints ranges the same way).
export { formatDay };

// A job's spec filters as the form's selections; line-item IDs take the order's current labels.
const fromSpecFilters = (filters = {}, lineItems = []) => {
	const opts = (value) => specValues(value).map((v) => ({ value: v, label: v }));
	const known = new Map(lineItems.map((li) => [li.value, li]));
	return {
		lineItem: specValues(filters.line_item).map((id) => known.get(id) || { value: id, label: String(id) }),
		country: opts(filters.country),
		device: opts(filters.device),
		domain: opts(filters.domain),
	};
};
const fromSpecEventType = (value) =>
	value ? { value, label: EVENT_TYPE_LABELS[value] || value } : ALL_EVENT_TYPES;
function BulkReportingTool({ tagId }) {
	const [options, setOptions] = useState(null);
	const [ordersLoading, setOrdersLoading] = useState(false);
	// "no_data" (the ArcTag has nothing in the window) or "error" (the load failed).
	const [ordersState, setOrdersState] = useState(null);
	const [order, setOrder] = useState(null);
	const [orderOptions, setOrderOptions] = useState(null);
	const [orderOptionsLoading, setOrderOptionsLoading] = useState(false);
	const [orderOptionsError, setOrderOptionsError] = useState("");
	const [range, setRange] = useState({ start: "", end: "" });
	const [filters, setFilters] = useState(EMPTY_FILTERS);
	const [eventType, setEventType] = useState(ALL_EVENT_TYPES);
	const [dataTypes, setDataTypes] = useState([]);
	const [dataTypesTouched, setDataTypesTouched] = useState(false);
	// The Checkbox group is uncontrolled: "Generate again" remounts it with the
	// job's sections checked.
	const [typesSeed, setTypesSeed] = useState({ key: 0, selected: [] });
	// "download" | "email" while a submit is in flight, else null.
	const [submitting, setSubmitting] = useState(null);
	const submittingRef = useRef(false);
	const [submitError, setSubmitError] = useState("");
	// One transient toast at a time; failure toasts from earlier visits queue behind it.
	const [toast, setToast] = useState(null);
	const toastSeq = useRef(0);
	const [failureToasts, setFailureToasts] = useState([]);
	// tag_id -> ArcTag name, for the failure toast (ASB-4360).
	const [tagNames, setTagNames] = useState({});
	// Table-level messages: an action's refusal, the deep link's outcome.
	const [notice, setNotice] = useState(null);
	const [deepLinkNotice, setDeepLinkNotice] = useState(null);
	// A job submitted on this page failed (ASB-3997's inline message).
	const [generationFailed, setGenerationFailed] = useState(false);
	const [busyIds, setBusyIds] = useState([]);
	const [highlightId, setHighlightId] = useState(null);
	const selectorRef = useRef(null);
	// "Generate again": the spec to apply once its campaign's options load.
	const pendingRefill = useRef(null);
	// The shared date input keeps its own value; these set it when the
	// campaign's flight arrives or the campaign changes.
	const startRef = useRef(null);
	const endRef = useRef(null);

	// Who is looking: the role from the session, the user id from the token.
	const [roleId] = useState(() => getSession()?.role_id ?? null);
	const [userId] = useState(getUserId);
	const isOwn = (job) => userId !== null && job?.submitted_by?.user_id === userId;

	const [searchParams, setSearchParams] = useSearchParams();
	const deepLinkId = searchParams.get("job");
	const location = useLocation();
	const locationKey = location.key;
	const navigate = useNavigate();

	const showToast = (status, title, message) => {
		const key = ++toastSeq.current;
		setToast({ key, status, title, message });
	};
	// Only this toast's own close or timer clears it, never a newer one's.
	const closeToast = (key) => setToast((t) => (t && t.key === key ? null : t));
	const setBusy = (jobId, busy) =>
		setBusyIds((prev) => (busy ? [...prev, jobId] : prev.filter((id) => id !== jobId)));

	// Jobs submitted on this page: id -> { download, handled }. A Download job
	// downloads itself when it finishes; either kind's failure shows inline.
	const sessionJobs = useRef(new Map());
	// One automatic download per job, whatever path reports it finished.
	const autoDownloaded = useRef(new Set());
	// Jobs seen queued or running on this page -> when last seen so (browser
	// clock), so a finish can be told to be seen live.
	const seenActive = useRef(new Map());

	// ASB-3994: the Order list is the ArcTag's own campaigns. The API checks
	// the caller may see the tag, so nothing from another ArcTag can appear.
	useEffect(() => {
		if (!tagId) return undefined;
		let cancelled = false;
		setOrdersLoading(true);
		setOrdersState(null);
		setOptions(null);
		setOrder(null);
		getOptions(tagId)
			.then((data) => {
				if (!cancelled) setOptions(data);
			})
			.catch((err) => {
				// No data in the window is an empty ArcTag; anything else is a
				// failed load, which must not claim the ArcTag is empty.
				if (!cancelled) setOrdersState(bulkReportErrorCode(err) === "no_data" ? "no_data" : "error");
			})
			.finally(() => {
				if (!cancelled) setOrdersLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [tagId]);

	const orders = options?.orders || [];
	const availableFrom = options?.available_from || "";
	const availableTo = options?.available_to || "";

	// ASB-3995: a chosen campaign seeds the date range with its flight, clamped
	// into the data window, and loads the values its filters can take.
	// Changing campaign clears both.
	useEffect(() => {
		setFilters(EMPTY_FILTERS);
		setEventType(ALL_EVENT_TYPES);
		setOrderOptions(null);
		setOrderOptionsError("");
		setSubmitError("");
		if (startRef.current) startRef.current.select("");
		if (endRef.current) endRef.current.select("");
		if (!order) {
			// The data types leave the page with the campaign and come back
			// unchecked, so the selection they held goes too.
			setDataTypes([]);
			setDataTypesTouched(false);
			setTypesSeed((prev) => ({ key: prev.key + 1, selected: [] }));
			return undefined;
		}
		let cancelled = false;
		setOrderOptionsLoading(true);
		getOrderOptions(tagId, order.value)
			.then((data) => {
				if (cancelled) return;
				setOrderOptions(data);
				const refill = pendingRefill.current?.order_id === order.value ? pendingRefill.current : null;
				pendingRefill.current = null;
				if (refill) {
					// "Generate again": the job's own dates and filters, as they were sent.
					if (startRef.current) startRef.current.select(refill.date_start || "");
					if (endRef.current) endRef.current.select(refill.date_end || "");
					setFilters(fromSpecFilters(refill.filters || {}, data.line_items || []));
					setEventType(fromSpecEventType(refill.filters?.event_type));
					return;
				}
				// Only the default is clamped; a date the user types is sent as is.
				if (startRef.current) startRef.current.select(clampDate(data.first_day, availableFrom, availableTo) || "");
				if (endRef.current) endRef.current.select(clampDate(data.last_day, availableFrom, availableTo) || "");
			})
			.catch((err) => {
				if (cancelled) return;
				pendingRefill.current = null;
				setOrderOptionsError(
					bulkReportErrorCode(err) === "order_not_found" ? bulkReportErrorMessage(err) : ORDER_OPTIONS_ERROR,
				);
			})
			.finally(() => {
				if (!cancelled) setOrderOptionsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [tagId, order]);

	const hasOrder = !!order;
	const orderReady = hasOrder && !!orderOptions;
	const rangeError = range.start && range.end && range.start > range.end ? RANGE_ERROR : "";
	const dataTypesError =
		hasOrder && dataTypesTouched && dataTypes.length === 0 ? DATA_TYPES_ERROR : "";

	// The actions appear once the selection is complete and valid.
	const canSubmit =
		orderReady && !!range.start && !!range.end && !rangeError && dataTypes.length > 0;

	const jobsList = useBulkReportJobs(tagId, { onJobs: (list) => handleJobs(list) });

	// A fresh presigned link per download: it lasts 300 s. 410 means the
	// report's 7 days are up, whatever the row said.
	const downloadJob = async (job, { announce }) => {
		setBusy(job.id, true);
		setNotice(null);
		try {
			const { url } = await getJobDownload(job.id);
			openDownload(url);
			if (announce) showToast("Success", "Report ready", REPORT_DOWNLOADING);
		} catch (err) {
			if (err?.status === 410 || bulkReportErrorCode(err) === "expired") {
				jobsList.markExpired(job.id);
				setNotice({ tone: "error", text: BULK_REPORT_ERRORS.expired });
			} else {
				setNotice({ tone: "error", text: bulkReportErrorMessage(err) });
			}
		} finally {
			setBusy(job.id, false);
		}
	};

	// A failure seen live is acknowledged now, so it does not toast again on
	// the next visit. Only the submitter may acknowledge.
	const acknowledgeSeen = (job) => {
		setFailureToasts((prev) => prev.filter((j) => j.id !== job.id));
		acknowledgeJob(job.id).catch(() => {
			// Not fatal: the failure toasts once more on the next visit.
		});
	};

	// Every list that lands passes through here: a job that has left queued /
	// running since this page saw it (or submitted it) is acted on once.
	function handleJobs(list) {
		list.forEach((job) => {
			if (isActiveJob(job)) {
				seenActive.current.set(job.id, Date.now());
				return;
			}
			const session = sessionJobs.current.get(job.id);
			const lastSeenActive = seenActive.current.get(job.id);
			const watched = seenActive.current.delete(job.id);
			const seenLive = watched && Date.now() - lastSeenActive <= AUTO_DOWNLOAD_WINDOW_MS;
			if (session) {
				if (session.handled) return;
				session.handled = true;
			} else if (!watched) {
				return;
			}
			if (job.status === "succeeded" && session?.download) {
				if (autoDownloaded.current.has(job.id)) return;
				autoDownloaded.current.add(job.id);
				if (seenLive) {
					downloadJob(job, { announce: true });
				} else {
					// Finished while the page was not looking: say so, do not download.
					showToast("Success", "Report ready", REPORT_READY_IN_TABLE);
				}
			} else if (job.status === "failed") {
				if (session) setGenerationFailed(true);
				if (isOwn(job)) acknowledgeSeen(job);
			}
		});
	}

	// ASB-3997 / ASB-3998: both buttons queue the same job; only Download
	// fetches the file when it is ready. No retry and no second click while
	// one is in flight, so a slow answer never queues two jobs.
	const submit = async (via) => {
		if (submittingRef.current) return;
		submittingRef.current = true;
		setSubmitting(via);
		setSubmitError("");
		setGenerationFailed(false);
		setNotice(null);
		try {
			const { job, reused } = await submitJob({
				tagId,
				orderId: order.value,
				dateStart: range.start,
				dateEnd: range.end,
				filters: toApiFilters({
					lineItem: filters.lineItem.map((o) => o.value),
					country: filters.country.map((o) => o.value),
					device: filters.device.map((o) => o.value),
					domain: filters.domain.map((o) => o.value),
					eventType: eventType?.value || null,
				}),
				sections: DATA_TYPES.map((t) => t.value).filter((v) => dataTypes.includes(v)),
				requestedVia: via,
			});
			if (job) {
				// An identical job already in progress comes back as is (202), so
				// a Download on it downloads it whichever button queued it.
				const known = sessionJobs.current.get(job.id);
				sessionJobs.current.set(job.id, {
					download: via === "download" || !!known?.download,
					handled: !!reused || !!known?.handled,
				});
				if (isActiveJob(job)) seenActive.current.set(job.id, Date.now());
				jobsList.addSubmitted(job);
			}
			if (reused && via === "download") {
				showToast("Success", "Report ready", REUSED_DOWNLOAD);
				if (job && !autoDownloaded.current.has(job.id)) {
					autoDownloaded.current.add(job.id);
					downloadJob(job, { announce: false });
				}
			} else if (reused) {
				showToast("Success", "Report ready", REUSED_EMAIL);
			} else if (via === "email") {
				showToast("Success", "Report requested", EMAIL_REQUESTED);
			}
		} catch (err) {
			setSubmitError(bulkReportErrorMessage(err));
		} finally {
			submittingRef.current = false;
			setSubmitting(null);
		}
	};

	// Queued or generating, per the role rule; the API refuses the rest.
	const cancel = async (job) => {
		setBusy(job.id, true);
		setNotice(null);
		try {
			jobsList.replaceJob(await cancelJob(job.id));
		} catch (err) {
			const code = bulkReportErrorCode(err);
			setNotice({ tone: "error", text: code === "not_owner" ? CANCEL_NOT_OWNER : bulkReportErrorMessage(err) });
			// 409: it finished meanwhile; show how.
			if (code === "terminal") jobsList.reload();
		} finally {
			setBusy(job.id, false);
		}
	};

	// "Generate again": the job's campaign, dates, filters and sections back in
	// the form. The dates and filters land once the campaign's options load.
	const generateAgain = (job) => {
		const spec = job.spec || {};
		const found = spec.order_id != null ? orders.find((o) => o.value === spec.order_id) : undefined;
		setNotice(null);
		if (!found) {
			setNotice({ tone: "error", text: GENERATE_AGAIN_MISSING });
			return;
		}
		pendingRefill.current = spec;
		const sections = DATA_TYPES.filter((t) => (spec.sections || []).includes(t.value));
		setDataTypes(sections.map((t) => t.value));
		setDataTypesTouched(true);
		setTypesSeed((prev) => ({ key: prev.key + 1, selected: sections }));
		// A new object, so choosing the same campaign again still reloads it.
		setOrder({ ...found });
		const el = selectorRef.current;
		if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	// ASB-3998: a report that failed since the last visit toasts once; closing
	// the toast acknowledges it. Best effort: if the read fails, the failure
	// is still listed next time. The list covers every ArcTag the user can
	// use, so the toast names the report's ArcTag (ASB-4360), from the same
	// list the header's switcher reads. The toast doesn't wait for that read:
	// until it lands, or if it fails, the toast names no ArcTag.
	useEffect(() => {
		let cancelled = false;
		getUnacknowledgedFailures()
			.then((list) => {
				if (cancelled) return;
				setFailureToasts(list);
				if (list.length === 0) return;
				getTags()
					.then((res) => {
						const tags = res?.data || [];
						if (!cancelled) setTagNames(Object.fromEntries(tags.map((t) => [t.tagId, t.name])));
					})
					.catch(() => {});
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	// viewFailure navigates once its acknowledge settles; not if the user has
	// left the tool by then.
	const mounted = useRef(true);
	useEffect(
		() => () => {
			mounted.current = false;
		},
		[]
	);

	const dismissFailure = (job) => {
		setFailureToasts((prev) => prev.filter((j) => j.id !== job.id));
		acknowledgeJob(job.id).catch(() => {});
	};

	// A failure on another ArcTag can't be found in this ArcTag's table, so its
	// toast links to it: ?job= switches to the report's ArcTag and highlights it
	// (demo.js, then the ?job= effect below). Following the link acknowledges
	// it, before the switch remounts this tool and reads the failures again.
	const jobLink = (job) => `${location.pathname}?job=${encodeURIComponent(job.id)}`;
	const viewFailure = (job) => {
		setFailureToasts((prev) => prev.filter((j) => j.id !== job.id));
		acknowledgeJob(job.id)
			.catch(() => {})
			.then(() => {
				if (mounted.current) navigate(jobLink(job));
			});
	};

	const { loaded: jobsLoaded, setPinned, setView: setJobsView, setPage: setJobsPage } = jobsList;

	// The deep link's report has done its job once the user browses or
	// navigates: unpin it and drop the highlight (ASB-4365).
	const clearDeepLink = useCallback(() => {
		setPinned(null);
		setHighlightId(null);
	}, [setPinned]);
	const onViewChange = useCallback(
		(next) => {
			clearDeepLink();
			setJobsView(next);
		},
		[clearDeepLink, setJobsView]
	);
	const onPageChange = useCallback(
		(next) => {
			clearDeepLink();
			setJobsPage(next);
		},
		[clearDeepLink, setJobsPage]
	);

	// Any navigation clears it too (the menu item links here without
	// remounting), except the tool's own ?job= removal. On a navigation to a
	// ?job= link this runs, but demo.js then unmounts the tool while it reads
	// that report's ArcTag, and the remounted tool starts clean.
	const ownNavigation = useRef(false);
	const seenLocation = useRef(locationKey);
	// Reset by a navigation, so the same link opened again shows again.
	const deepLinkDone = useRef(null);
	const scrolledTo = useRef(null);
	useEffect(() => {
		if (seenLocation.current === locationKey) return;
		seenLocation.current = locationKey;
		if (ownNavigation.current) {
			ownNavigation.current = false;
			return;
		}
		deepLinkDone.current = null;
		scrolledTo.current = null;
		clearDeepLink();
		setDeepLinkNotice(null);
	}, [locationKey, clearDeepLink]);

	// Once the link's report is shown (or its message is), drop ?job= so a
	// refresh or Back doesn't show it again. Only after the outcome lands: the
	// ?job= effect's cleanup would discard a getJob still in flight.
	const dropJobParam = useCallback(() => {
		ownNavigation.current = true;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("job");
				return next;
			},
			{ replace: true }
		);
	}, [setSearchParams]);

	// ?job=<id>: once the first page has loaded, highlight that row; if it is
	// not on this page or in this filter, read it and show it on top. A job on
	// another ArcTag, or one that cannot be read, gets a message instead.
	// Not the pinned row: a navigation back to its ?job= unpins it in this
	// same commit, so it is read and pinned again.
	const rowsRef = useRef(jobsList.rows);
	rowsRef.current = jobsList.rows.filter((j) => j.id !== jobsList.pinnedId);
	useEffect(() => {
		if (!deepLinkId || !jobsLoaded || deepLinkDone.current === deepLinkId) return undefined;
		deepLinkDone.current = deepLinkId;
		setDeepLinkNotice(null);
		if (rowsRef.current.some((j) => j.id === deepLinkId)) {
			setHighlightId(deepLinkId);
			dropJobParam();
			return undefined;
		}
		let cancelled = false;
		getJob(deepLinkId)
			.then((job) => {
				if (cancelled) return;
				if (!job || job.tag_id !== tagId) {
					setDeepLinkNotice({ tone: "info", text: DEEP_LINK_OTHER_TAG });
				} else {
					setPinned(job);
					setHighlightId(job.id);
				}
				dropJobParam();
			})
			.catch(() => {
				if (cancelled) return;
				setDeepLinkNotice({ tone: "error", text: DEEP_LINK_MISSING });
				dropJobParam();
			});
		return () => {
			cancelled = true;
		};
	}, [deepLinkId, jobsLoaded, tagId, setPinned, dropJobParam]);

	// Bring the highlighted row into view, once.
	const highlightShown = jobsList.rows.some((j) => j.id === highlightId);
	useEffect(() => {
		if (!highlightId || !highlightShown || scrolledTo.current === highlightId) return;
		scrolledTo.current = highlightId;
		const row = document.querySelector(".bulk-report-jobs__row--highlight");
		if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "center" });
	}, [highlightId, highlightShown]);

	const canCancel = useCallback((job) => canCancelBulkReport(roleId, job, userId), [roleId, userId]);

	// A refusal of the last submit no longer applies once the dates change.
	useEffect(() => {
		setSubmitError("");
	}, [range.start, range.end]);

	const onDataType = (e) => {
		const { value, checked } = e.target;
		setDataTypesTouched(true);
		setDataTypes((prev) => (checked ? [...prev, value] : prev.filter((v) => v !== value)));
	};

	const orderPlaceholder = ordersLoading
		? "Loading campaigns"
		: ordersState === "error"
			? "" // The field's error line says it; once is enough.
			: orders.length
				? "Select a campaign"
				: NO_CAMPAIGNS;

	const filtersDisabled = !orderReady;
	const filtersPlaceholder = (all) => (orderOptionsLoading ? "Loading" : all);

	const failure = failureToasts[0];
	const failureElsewhere = Boolean(failure) && failure.tag_id !== tagId;

	return (
		<div
			className="bulk-reporting-tool"
			data-testid="bulk-reporting-tool"
		>
			<section
				ref={selectorRef}
				className="bulk-report-selector"
				aria-labelledby="bulk-report-selector-title"
			>
				<h2
					id="bulk-report-selector-title"
					className="bulk-report-selector__title"
				>
					Report criteria
				</h2>
				<div className="bulk-report-selector__grid">
					<FieldGroup
						label="Order"
						htmlFor="bulk-order"
						className="span-2"
						error={ordersState === "error" ? ORDERS_ERROR : orderOptionsError || false}
					>
						<SelectField
							name="bulk-order"
							required={false}
							disabled={ordersLoading || orders.length === 0}
							search
							options={orders}
							selected={order}
							onSubmit={(sel) => setOrder(sel || null)}
							placeholder={orderPlaceholder}
						/>
						{hasOrder && (
							<p
								className="bulk-report-selector__advertiser"
								data-testid="bulk-report-advertiser"
							>
								Advertiser: <span>{order.advertiser_label || "Not set"}</span>
							</p>
						)}
					</FieldGroup>
					<FieldGroup
						label="Date Range Start"
						htmlFor="bulk-range-start"
						error={!!rangeError}
					>
						<Basic
							ref={startRef}
							name="bulk-range-start"
							type="date"
							required={false}
							disabled={!orderReady}
							min={availableFrom || undefined}
							max={availableTo || undefined}
							value={range.start}
							onInput={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
						/>
						{availableFrom && (
							<p
								className="bulk-report-selector__window"
								data-testid="bulk-report-window"
							>
								Data available from {formatDay(availableFrom)}
							</p>
						)}
					</FieldGroup>
					<FieldGroup
						label="Date Range End"
						htmlFor="bulk-range-end"
						error={rangeError || false}
					>
						<Basic
							ref={endRef}
							name="bulk-range-end"
							type="date"
							required={false}
							disabled={!orderReady}
							min={availableFrom || undefined}
							max={availableTo || undefined}
							value={range.end}
							onInput={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
						/>
					</FieldGroup>
					{FILTERS.map((f) => (
						<FieldGroup
							key={f.key}
							label={f.label}
							htmlFor={`bulk-${f.key}`}
						>
							<SelectField
								name={`bulk-${f.key}`}
								required={false}
								disabled={filtersDisabled}
								multi
								search
								options={toOptions(orderOptions?.[f.source])}
								selected={filters[f.key]}
								onSubmit={(sel) => setFilters((prev) => ({ ...prev, [f.key]: sel || [] }))}
								placeholder={filtersPlaceholder(f.all)}
							/>
						</FieldGroup>
					))}
					<FieldGroup
						label="Event Type"
						htmlFor="bulk-eventType"
					>
						<SelectField
							name="bulk-eventType"
							required={false}
							disabled={filtersDisabled}
							options={toEventTypeOptions(orderOptions?.event_types)}
							selected={eventType}
							onSubmit={(sel) => setEventType(sel || ALL_EVENT_TYPES)}
							placeholder={filtersPlaceholder("All")}
						/>
					</FieldGroup>
				</div>
			</section>

			{hasOrder && (
				<section
					className="bulk-report-types"
					aria-labelledby="bulk-report-types-title"
				>
					<FieldGroup
						label={
							<span
								id="bulk-report-types-title"
								className="bulk-report-types__title"
							>
								Data to include
							</span>
						}
						error={dataTypesError || false}
					>
						<Checkbox
							key={typesSeed.key}
							name="bulk-data-types"
							options={DATA_TYPES}
							selected={typesSeed.selected}
							required={false}
							disabled={!orderReady}
							style="row"
							onInput={onDataType}
						/>
					</FieldGroup>
				</section>
			)}

			{canSubmit && (
				<div
					className="bulk-report-actions"
					data-testid="bulk-report-actions"
				>
					{submitError && (
						<p
							className="bulk-report-actions__error"
							role="alert"
						>
							{submitError}
						</p>
					)}
					<Button
						text="Email Me My Report"
						type="button"
						style="light"
						disabled={!!submitting}
						loaded={submitting === "email"}
						onClick={() => submit("email")}
					/>
					<Button
						text="Download My Report"
						type="button"
						style="dark"
						disabled={!!submitting}
						loaded={submitting === "download"}
						onClick={() => submit("download")}
					/>
				</div>
			)}

			<div className="bulk-report-jobs-messages">
				{generationFailed && (
					<p
						className="bulk-report-message bulk-report-message--error"
						role="alert"
						data-testid="bulk-report-generation-failed"
					>
						{GENERATION_FAILED}
					</p>
				)}
				{[deepLinkNotice, notice].filter(Boolean).map((n) => (
					<p
						key={n.text}
						className={`bulk-report-message bulk-report-message--${n.tone}`}
						role={n.tone === "error" ? "alert" : "status"}
					>
						{n.text}
					</p>
				))}
				{jobsList.pollError && jobsList.rows.length > 0 && (
					<p
						className="bulk-report-message bulk-report-message--error"
						role="alert"
					>
						{jobsList.pollError}
					</p>
				)}
			</div>

			<BulkReportJobsTable
				rows={jobsList.rows}
				view={jobsList.view}
				onViewChange={onViewChange}
				page={jobsList.page}
				onPageChange={onPageChange}
				pagination={jobsList.pagination}
				loading={jobsList.loading}
				loadError={jobsList.loadError}
				onRetry={jobsList.reload}
				highlightId={highlightId}
				busyIds={busyIds}
				canCancel={canCancel}
				onDownload={(job) => downloadJob(job, { announce: false })}
				onGenerateAgain={generateAgain}
				onCancel={cancel}
			/>

			{toast ? (
				<Toast
					key={`toast-${toast.key}`}
					status={toast.status}
					title={toast.title}
					message={toast.message}
					callback={() => closeToast(toast.key)}
					autoHide={true}
				/>
			) : (
				failure && (
					<Toast
						key={`failure-${failure.id}`}
						status="Invalid"
						title="Report failed"
						message={failureToastMessage(failure, {
							// The job names its own campaign, on any ArcTag (ASB-4726).
							campaign: campaignLabel(failure),
							arcTag: tagNames[failure.tag_id],
						})}
						action={
							failureElsewhere ? (
								<Link
									to={jobLink(failure)}
									onClick={(e) => {
										// A modified click opens the link as usual (a new tab or window),
										// where it is shown: acknowledge it here so it doesn't toast again.
										// (Alt-click downloads the link in Chrome: not a modifier here.)
										if (e.metaKey || e.ctrlKey || e.shiftKey) {
											dismissFailure(failure);
											return;
										}
										e.preventDefault();
										viewFailure(failure);
									}}
									// A middle-click fires auxclick, not click: a new tab too.
									onAuxClick={(e) => {
										if (e.button === 1) dismissFailure(failure);
									}}
								>
									View report
								</Link>
							) : null
						}
						callback={() => dismissFailure(failure)}
						autoHide={false}
					/>
				)
			)}
		</div>
	);
}

BulkReportingTool.propTypes = {
	// The active ArcTag's numeric tag_id (localStorage activeSilo, parsed).
	tagId: PropTypes.number.isRequired,
};

export default BulkReportingTool;
